import React from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import {useThemeMode, systemMode} from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider';

type ModernStatusChartProps = {
    // New: pre-computed values (Admin Dashboard)
    statusData?: { open: number; inProgress: number };
    // Old: raw data array (Manager/Supervisor/Finance)
    jobs?: any[];
    loading?: boolean;
};

export const ModernStatusChart: React.FC<ModernStatusChartProps> = ({ statusData, jobs, loading }) => {
    const {mode} = useThemeMode();
    const isDarkMode = mode === 'dark' || (mode === 'system' && systemMode === 'dark');

    let openCount = 0, progressCount = 0;
    if (statusData) {
        openCount = statusData.open || 0;
        progressCount = statusData.inProgress || 0;
    } else if (jobs) {
        for (const j of jobs) {
            if (j.status === 'OPEN') openCount++;
            else if (j.status === 'IN_PROGRESS') progressCount++;
        }
    }

    const series = [openCount, progressCount];
    const labels = ['Open', 'In Progress'];
    const colors = ['#009ef7', '#ffc700']; // Primary, Warning

    const chartOptions: ApexOptions = {
        chart: {
            type: 'donut',
            fontFamily: 'inherit'
        },
        labels: labels,
        colors: colors,
        plotOptions: {
            pie: {
                donut: {
                    size: '65%',
                    labels: {
                        show: true,
                        name: {
                            show: true,
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#a1a5b7'
                        },
                        value: {
                            show: true,
                            fontSize: '24px',
                            fontWeight: 700,
                            color: isDarkMode ? '#ffffff' : '#181c32',
                            formatter: function (val) {
                                return val.toString();
                            }
                        },
                        total: {
                            show: true,
                            showAlways: true,
                            label: 'Active',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#a1a5b7',
                            formatter: function (w) {
                                return w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0).toString();
                            }
                        }
                    }
                }
            }
        },
        dataLabels: {
            enabled: false
        },
        legend: {
            show: true,
            position: 'bottom',
            horizontalAlign: 'center',
            fontSize: '14px',
            markers: {
                size: 6,
                strokeWidth: 0,
            },
            itemMargin: {
                horizontal: 10,
                vertical: 5
            },
            labels: {
                colors: isDarkMode ? '#ffffff' : '#181c32',
                useSeriesColors: false
            }
        },
        stroke: {
            show: true,
            width: 0,
            colors: ['transparent']
        },
        states: {
            hover: {
                filter: {
                    type: 'none'
                }
            },
            active: {
                allowMultipleDataPointsSelection: false,
                filter: {
                    type: 'none'
                }
            }
        },
        tooltip: {
            enabled: true,
            theme: 'light'
        }
    };

    return (
        <div className="card h-100" style={{ backgroundColor: isDarkMode ? '#1e1e2d' : '' }}>
            <div className="card-header border-0 pt-5">
                <h3 className="card-title align-items-start flex-column">
                    <span className={`card-label fw-bolder fs-3 ${isDarkMode ? 'text-white' : 'text-dark'}`}>Status Overview</span>
                    <span className="text-muted mt-1 fw-bold fs-7">Real-time job distribution</span>
                </h3>
                <div className="card-toolbar">
                    <button className="btn btn-sm btn-icon btn-color-gray-400 btn-active-color-primary">
                        <i className="bi bi-three-dots fs-3"></i>
                    </button>
                </div>
            </div>
            <div className="card-body pt-3 pb-0">
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{height: '250px'}}>
                         <span className="spinner-border text-primary"></span>
                    </div>
                ) : (
                    <div className="d-flex flex-center position-relative h-250px">
                        <Chart options={chartOptions} series={series} type="donut" height={250} width="100%" />
                    </div>
                )}
            </div>
        </div>
    );
};
